'use client';

import React, { useState } from 'react';
import type { LucideIcon } from 'lucide-react';

import type { PartnerLogo } from '@/config/partnerLogos';
import { cn } from '@/lib/utils';

/**
 * Brand lockup: icon + name for marks and our own icons, the wordmark alone
 * for logos that already spell the name. Falls back to the name if the logo fails.
 */
export function BrandLockup({
  label,
  logo,
  icon: Icon,
  size = 'md',
  className,
}: {
  label: string;
  logo?: PartnerLogo;
  icon?: LucideIcon;
  size?: 'md' | 'lg';
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const shownLogo = logo && !failed ? logo : null;
  const tile = size === 'lg' ? 'h-10 w-10' : 'h-8 w-8';

  if (shownLogo?.kind === 'wordmark') {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={shownLogo.src}
        alt={label}
        className={cn(
          'max-w-full object-contain object-left',
          size === 'lg' ? 'h-8 max-w-[220px]' : 'h-6',
          className
        )}
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span className={cn('flex min-w-0 items-center gap-2.5', className)}>
      {Icon ? (
        <span
          className={cn(
            'flex shrink-0 items-center justify-center rounded-md border border-border/80 bg-muted text-foreground/70',
            tile
          )}
          aria-hidden
        >
          <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
        </span>
      ) : shownLogo ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={shownLogo.src}
          alt=""
          aria-hidden
          className={cn('shrink-0 object-contain', tile)}
          onError={() => setFailed(true)}
        />
      ) : null}
      <span
        className={cn(
          'truncate font-medium text-[var(--charcoal)]',
          size === 'lg' ? 'text-base' : 'text-sm'
        )}
      >
        {label}
      </span>
    </span>
  );
}
